import { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import api from '../helpers/api';

const ToolCheckinModal = ({ isOpen, toggle, checkoutId, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conditionOptions, setConditionOptions] = useState([]);

  const [formData, setFormData] = useState({
    conditionAtReturn: '',
    returnNotes: ''
  });

  const fetchConditions = async () => {
    try {
      const { data } = await api.get('dynamic-enum/find-by-name?name=condition');
      const values = data?.response?.values || [];
      setConditionOptions(values);
      // Default to 'Good' if available
      const defaultVal = values.includes('Good') ? 'Good' : values[0] || '';
      setFormData(prev => ({ ...prev, conditionAtReturn: defaultVal }));
    } catch (err) {
      console.error('Error fetching conditions:', err);
      const fallback = ['New', 'Good', 'Fair', 'Poor', 'Damaged'];
      setConditionOptions(fallback);
      setFormData(prev => ({ ...prev, conditionAtReturn: 'Good' }));
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchConditions();
      setFormData({
        conditionAtReturn: '',
        returnNotes: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      setLoading(true);
      const payload = {
        id: checkoutId,
        conditionAtReturn: formData.conditionAtReturn,
        returnNotes: formData.returnNotes
      };

      await api.post('work-order-tool/checkin', payload);

      if (onSuccess) onSuccess();
      toggle();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || t('shared.something_went_wrong', 'Something went wrong!');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const getConditionLabel = (condition) => {
    return t(`shared.condition_${condition.toLowerCase()}`, condition);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <div className="modal-header">
        <h5 className="modal-title">
          <i className="bx bx-log-in me-2"></i>
          {t('business.tools.check_in', 'Check In')}
        </h5>
        <button type="button" className="btn-close" onClick={toggle} aria-label={t('shared.close', 'Close')} />
      </div>

      <div className="modal-body">
        <form onSubmit={handleSubmit}>
          {/* Condition at Return */}
          <Row className="mb-3">
            <label htmlFor="conditionReturn" className="col-sm-4 col-form-label">
              {t('business.tools.condition_at_return', 'Condition at Return')}
            </label>
            <Col sm={8}>
              <select
                id="conditionReturn"
                className="form-select"
                value={formData.conditionAtReturn}
                onChange={(e) => setFormData({ ...formData, conditionAtReturn: e.target.value })}
              >
                {conditionOptions.map(c => (
                  <option key={c} value={c}>{getConditionLabel(c)}</option>
                ))}
              </select>
            </Col>
          </Row>

          {/* Return Notes */}
          <Row className="mb-3">
            <label htmlFor="returnNotes" className="col-sm-4 col-form-label">
              {t('business.tools.return_notes', 'Return Notes')}
            </label>
            <Col sm={8}>
              <textarea
                id="returnNotes"
                className="form-control"
                rows="3"
                value={formData.returnNotes}
                onChange={(e) => setFormData({ ...formData, returnNotes: e.target.value })}
                placeholder={t('business.tools.return_notes_placeholder', 'Add any notes about the tool condition...')}
              />
            </Col>
          </Row>

          {error && <div className="alert alert-danger">{error}</div>}

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-secondary" onClick={toggle} disabled={loading}>
              {t('shared.cancel', 'Cancel')}
            </button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('shared.processing', 'Processing...')}
                </>
              ) : (
                <>
                  <i className="bx bx-log-in me-1"></i>
                  {t('business.tools.check_in', 'Check In')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ToolCheckinModal;
